import React, { useState, useEffect, useRef } from "react";
import Icono from "./CTIcons";
import { Loader2 } from "lucide-react";
import { displayDate,strhor, ctod } from "lib/dates";
import { intToCssColor } from "lib/color";
import { useSNAP } from "hooks/useSNAP";

/* ------------------- Cache global para imágenes ------------------- */
const imageCache = new Map();
const TTL = 10 * 60 * 1000; // 10 minutos

const getCachedImage = (id) => {
  const entry = imageCache.get(id);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > TTL) {
    URL.revokeObjectURL(entry.blobUrl);
    imageCache.delete(id);
    return null;
  }
  return entry.blobUrl;
};

const setCachedImage = (id, blob) => {
  const blobUrl = URL.createObjectURL(blob);
  imageCache.set(id, { blobUrl, timestamp: Date.now() });
  return blobUrl;
};

/* ------------------- Renderers individuales ------------------- */

// 🔹 Número formateado
const CellNumber = ({ value, col }) => {
  if (value == null || value === "") return "";
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: col.dec ?? 2,
    maximumFractionDigits: col.dec ?? 2,
    useGrouping: true,
  }).format(Number(value));
};


// 🔹 Fecha
const CellDate = ({ value, col }) => {
  if (!value) return "";
    // Aprovechamos tu función general
  return displayDate(value, { 
    TYPE: col?.typefield, 
    TYPEEDIT: col?.type 
  });
 
};

// 🔹 Hora
const CellHour = ({ value,col }) => {
  if(col?.typefield===2)
   return displayDate(value, { 
    TYPE: col?.typefield, 
    TYPEEDIT: col?.type 
  });
  if(col?.type===10) return value == null ? "" : strhor(value,false);
  
  return value == null ? "" : strhor(value,true);
};

// 🔹 Color
const CellColor = ({ value, col }) => {
  const cssColor = intToCssColor(value);
  const width = col.imageSize?.width || "10rem";
  const height = col.imageSize?.height || "10rem";
  return (
    <div
      className="flex justify-center items-center w-full"
      style={{ minHeight: height }}
    >
      <div
        className="rounded-xl shadow-md border"
        style={{
          backgroundColor: cssColor,
          width,
          height,
        }}
      />
    </div>
  );
};

// 🔹 Icono
const CellIcon = ({ value }) => {
  return value ? <Icono name={String(value)} size={20} /> : null;
};

// 🔹 Imagen (con carga SNAP + caché + abort)
const CellImage = ({ value, col }) => {
  const [src, setSrc] = useState(() => getCachedImage(value));
  const snap = useSNAP({ useUserId: true, useToken: true });
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!value) return;
    if (src) return; // Ya en caché

    const queryString = col.funcion?.split("?")[1] || "";
    const params = new URLSearchParams(queryString);
    const metode = params.get("metode") || "getimage";

    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    const fetchImage = async () => {
      try {
        snap.setMetodo("GET");
        snap.setFuncion(col.funcion);
        snap.setParam("metode", metode);
        snap.setParam("id", value);
        const res = await snap.fetchData({ signal });

        if (res?.Blob && res.ContentType?.startsWith("image/")) {
          const url = setCachedImage(value, res.Blob);
          setSrc(url);
        } else {
          setSrc(null);
        }
      } catch (err) {
        if (err.name !== "AbortError")
          console.error("CTCell image load error:", err);
        setSrc(null);
      }
    };

    fetchImage();

    return () => controller.abort();
  }, [value, col.funcion, snap]);

  const width = col.imageSize?.width || "10rem";
  const height = col.imageSize?.height || "10rem";

  if (src === undefined)
    return (
      <div
        className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-xl"
        style={{ width, height }}
      >
        <Loader2 className="animate-spin w-6 h-6 text-gray-500" />
      </div>
    );

  if (src === null)
    return (
      <div
        className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-xl"
        style={{ width, height }}
      >
        No disponible
      </div>
    );

  return (
    <div
      className="flex items-center justify-center w-full"
      style={{ minHeight: height }}
    >
      <img
        src={src}
        alt={col.header}
        className="object-contain rounded-xl shadow-md"
        style={{ maxWidth: width, maxHeight: height }}
      />
    </div>
  );
};

// 🔹 Checkbox
const CellCheckbox = ({ value, toggleRow, row }) => (
  <input
    type="checkbox"
    checked={!!value}
    onChange={() => toggleRow?.(row)}
  />
);

// 🔹 Contenido por defecto (texto)

const CellDefault = ({ value }) => value ?? "";

// 🔹 HTML o contenido libre
const CellHTML = ({ row, col }) => {
  if (typeof col.freeDraw === "function") {
    return <>{col.freeDraw(row)}</>;
  }
  return <>{row[col.accessor] ?? ""}</>;
};

/* ------------------- Mapa de renderers ------------------- */
const renderers = {
  
  4: CellDate,
  5: CellNumber,
  8: CellImage,
  9: CellIcon,
  10: CellHour,
  11: CellColor,
  12: CellDate,
  13: CellHour,
  check: CellCheckbox,
  HTML: CellHTML,
  default: CellDefault,
};


const WithIcon = ({ children, colIndex, row, col, defaultIcon, getIcon }) => {
  const iconNumber = getIcon?.(row, col) ?? -1;
  const iconToRender = colIndex === 0 
    ? (iconNumber !== -1 ? iconNumber : defaultIcon)
    : -1;

  return (
    <span className="inline-flex items-center gap-2">
      {iconToRender !== -1 && <Icono name={String(iconToRender)} size={20} />}
      {children}
    </span>
  );
};

/* ------------------- Componente principal ------------------- */
export default function CTCell({
  row,
  col,
  colIndex,
  defaultIcon,
  getIcon,
  toggleRow,
}) {
  const value = row[col.accessor];
  const Renderer = renderers[col.type] || renderers.default;

  const content = (
    <Renderer
      value={value}
      row={row}
      col={col}
      colIndex={colIndex}
      defaultIcon={defaultIcon}
      getIcon={getIcon}
      toggleRow={toggleRow}
    />
  );

  return (
    <WithIcon
      colIndex={colIndex}
      row={row}
      col={col}
      defaultIcon={defaultIcon}
      getIcon={getIcon}
    >
      {content}
    </WithIcon>
  );
}
